import type { QueryClient } from "@tanstack/react-query";

import type * as ozon from "@/api/ozonSupply";

/** Ключи запросов раздела и одна точка обновления направления.
 *
 *  ⚠️ Направление живёт теперь В ДВУХ местах кэша — в списке фиксации и на своей странице, — и
 *  ручки по-прежнему возвращают ПОЛНОЕ направление. Клади его только через `putDirection`: правка
 *  одного места оставила бы второе со старыми числами, и та же поставка на списке и на странице
 *  показывала бы разные бейджи.
 */
/** Список фиксаций — тот же ключ, что у «Отгрузок» и распределения (они его инвалидируют). */
export const RUNS_KEY = ["supply", "shipment-runs"] as const;

/** Реестр поставок по всем фиксациям: живые и архив — два запроса под одним префиксом. */
export const REGISTRY = ["supply", "ozon", "registry"] as const;

export const registryKey = (archived: boolean): unknown[] => [
  ...REGISTRY,
  archived,
];

export const dirKey = (shipmentId: number): unknown[] => [
  "supply",
  "ozon",
  "direction",
  shipmentId,
];

/** План паллет поставки в портале склада (`PalletPlanBanner`). Его меняют цикл 1С (запись плана)
 *  и применение правки (последний шаг каскада) — обе мутации обязаны его инвалидировать. */
export const palletPlanKey = (shipmentId: number): unknown[] => [
  "supply",
  "ozon",
  "pallet-plan",
  shipmentId,
];

export const placesKey = (shipmentId: number): unknown[] => [
  "supply",
  "ozon",
  "places",
  shipmentId,
];

/** Положить свежее направление во ВСЕ его места в кэше.
 *
 *  Списки (и реестр) обновляем по префиксу, не зная фиксации: поставка принадлежит ровно одной, а с какой
 *  из них сейчас открыт список — дело экрана, а не мутации. Деталь дополняем поверх старой, чтобы
 *  не потерять `run_id`: ручки действий его не возвращают. Записи нет в кэше — значит страница не
 *  открыта, и заводить её здесь нечего.
 */
export function putDirection(qc: QueryClient, d: ozon.OzonDirection): void {
  // ⚠️ В реестр — СЛИЯНИЕМ: ручки действий отдают направление без полей реестра (фиксация,
  // статус площадки, группа, архив), и замена строки целиком стёрла бы их
  qc.setQueriesData<ozon.OzonRegistryRow[]>(
    { queryKey: REGISTRY },
    (old) =>
      old &&
      old.map((x) => (x.shipment_id === d.shipment_id ? { ...x, ...d } : x)),
  );
  qc.setQueryData<ozon.OzonShipment>(
    dirKey(d.shipment_id),
    (old) => old && { ...old, ...d },
  );
  // ручка действия не отдаёт поля реестра (group/archived/ozon_order_state) — их досчитывает бэк,
  // поэтому реестр перечитываем, а слияние выше лишь не даёт строке мигать старыми числами
  void qc.invalidateQueries({ queryKey: REGISTRY });
}

/** Мониторинг мест поставки (активный или последний). */
export const slotWatchKey = (shipmentId: number): unknown[] => [
  "supply",
  "ozon",
  "slot-watch",
  shipmentId,
];

/** Журнал мониторинга — свой ключ на мониторинг: у поставки их может быть несколько по очереди. */
export const slotWatchEventsKey = (watchId: number): unknown[] => [
  "supply",
  "ozon",
  "slot-watch-events",
  watchId,
];

/** Активные мониторинги — отметка в реестре. */
export const ACTIVE_SLOT_WATCHES = [
  "supply",
  "ozon",
  "slot-watches",
  "active",
] as const;
